import { Balance, IosShare } from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  Collapse,
  Divider,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import { copyToClipboard } from "~/lib/clipboard";
import { Caption } from "~/pages/security/policies/components/Caption";
import { Email as EmailIcon } from "@mui/icons-material";
import { PolicyDownloadButton } from "../policy-download-button";
import { MSelect } from "../Select/Select";
import { ShowMoreSummary } from "../ShowMore/ShowMoreSummary";
import { Flex } from "~/components/Flex";
import { useState } from "react";
import { usePolicyViewer } from "./hooks/usePolicyViewer";
import { Policy, PolicyViewerProps } from "./policy-gql";
import { Space } from "~/lib/types";
import { TrustLevel } from "~/operations";
import { PolicyAssignSelect } from "./PolicyAssignSelect";
import { PolicyDeleteButton } from "./PolicyDeleteButton";
import { ScopeType, SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { CanAssign } from "~/lib/iam/actions/cnspec/policy/PolicyResolver/Assign";
import { CanDeletePolicy } from "~/lib/iam/actions/cnspec/policy/PolicyHub/DeletePolicy";
import { LinkIcon } from "~/icons";
import { CircularIconButton } from "../CircularIconButton/CircularIconButton";
import { IconButtonType } from "../CircularIconButton/DynamicButtonIcon";

type PolicyHeaderProps = {
  space: Space;
  scope: SpaceOrWorkspaceScope;
  policy: Policy;
  showScoringSystem: ReturnType<typeof usePolicyViewer>["showScoringSystem"];
  setShowScoringSystem: ReturnType<
    typeof usePolicyViewer
  >["setShowScoringSystem"];
  onAssignChange: PolicyViewerProps["onAssignChange"];
  scoringSystem: ReturnType<typeof usePolicyViewer>["scoringSystem"];
  scoreByMenuItems: ReturnType<typeof usePolicyViewer>["scoreByMenuItems"];
  handleChangeScoringSystem: (event: SelectChangeEvent<unknown>) => void;
};

export function PolicyHeader({
  space,
  scope,
  policy,
  showScoringSystem,
  setShowScoringSystem,
  onAssignChange,
  scoringSystem,
  scoreByMenuItems,
  handleChangeScoringSystem,
}: PolicyHeaderProps) {
  const [shareAnchorEl, setShareAnchorEl] = useState<null | HTMLElement>(null);
  const shareOpen = Boolean(shareAnchorEl);
  const shareLink = window.location.href;
  const shareEmailLink = `mailto:hello@mondoo.com?subject=${encodeURIComponent("Take a look at this policy")}&body=${encodeURIComponent(shareLink)}`;
  const isCustomPolicy = policy.trustLevel === TrustLevel.Private;

  return (
    <>
      <Box
        id="policy-header"
        mb={3}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Flex flexDirection="column">
          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
            textTransform="uppercase"
            sx={{ mb: 1 }}
          >
            {policy.name}
          </Typography>
          <Caption
            trustLevel={policy.trustLevel}
            version={policy.version}
            authors={policy.authors}
            totalChecks={policy.statistics?.checks}
          />
        </Flex>
        <Flex id="policy-actions" alignItems="center" gap={4}>
          <CanAssign>
            <Tooltip title="Score weighting" placement="top" arrow>
              <CircularIconButton
                onClick={() => setShowScoringSystem(!showScoringSystem)}
                iconType={IconButtonType.Balance}
                title="Score weighting"
                sx={{
                  minWidth: "unset",
                  ...(showScoringSystem
                    ? {
                        color: "primary.main",
                        bgcolor: (theme) =>
                          alpha(theme.palette.primary.main, 0.2),
                      }
                    : {}),
                }}
              />
            </Tooltip>
          </CanAssign>
          <Tooltip title="Share" placement="top" arrow>
            <CircularIconButton
              onClick={(e) => setShareAnchorEl(e.currentTarget)}
              sx={{ minWidth: "unset" }}
              title="Share"
              iconType={IconButtonType.IosShare}
            />
          </Tooltip>
          <Menu
            anchorEl={shareAnchorEl}
            open={shareOpen}
            onClose={() => setShareAnchorEl(null)}
            onClick={() => setShareAnchorEl(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            <MenuItem onClick={() => copyToClipboard(shareLink)}>
              <ListItemIcon>
                <LinkIcon fontSize="inherit" />
              </ListItemIcon>
              <ListItemText>Copy link</ListItemText>
            </MenuItem>
            <MenuItem component="a" href={shareEmailLink}>
              <ListItemIcon>
                <EmailIcon fontSize="inherit" />
              </ListItemIcon>
              <ListItemText>Email</ListItemText>
            </MenuItem>
          </Menu>
          <PolicyDownloadButton policy={policy} size="small" iconOnly />

          {scope.type === ScopeType.Space && (
            <>
              <CanDeletePolicy>
                <Divider orientation="vertical" flexItem />
                <PolicyDeleteButton
                  space={space}
                  policyMrn={policy.mrn}
                  isCustom={isCustomPolicy}
                />
              </CanDeletePolicy>
              <CanAssign>
                <PolicyAssignSelect
                  spaceMrn={space.mrn}
                  policyMrn={policy.mrn}
                  action={policy.action}
                  onChange={onAssignChange}
                />
              </CanAssign>
            </>
          )}
        </Flex>
      </Box>
      <CanAssign>
        <Collapse in={showScoringSystem}>
          <Flex sx={{ flexDirection: "column", gap: 1 }}>
            <Flex sx={{ gap: 0.5, alignItems: "center" }}>
              <Balance fontSize="inherit" />
              <Typography sx={{ fontWeight: 700, mb: 0 }}>
                Score weighting
              </Typography>
            </Flex>
            <Flex sx={{ gap: 3 }}>
              <Box>
                <Typography sx={{ color: "text.secondary", mb: 1 }}>
                  Choose how Mondoo calculates asset security scores with this
                  policy. To learn about scoring methods, read the{" "}
                  <Link
                    href="https://mondoo.com/docs/security/customize/score"
                    target="_blank"
                    rel="noopener"
                  >
                    Mondoo documentation
                  </Link>
                  .
                </Typography>
              </Box>
              <MSelect
                id="score-by"
                label="Score by"
                value={scoringSystem}
                menuItems={scoreByMenuItems}
                onChange={handleChangeScoringSystem}
              />
            </Flex>
            <Divider sx={{ my: 2 }} />
          </Flex>
        </Collapse>
      </CanAssign>
      {policy.docs && (
        <ShowMoreSummary
          maxHeight={165}
          text={policy.docs}
          externalLinksInNew
        />
      )}
    </>
  );
}
